import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import useInterval from 'hooks/useInterval';
import { LIVE } from 'constants/GameStatuses';
import { useUser } from 'stores/userStore';
import queries, { GamePickEmQuery, GamesShowQuery, rubyQueries } from 'queries';
import { createContainer } from 'containers';
import { localCache } from 'helpers/cache';
import merge from 'helpers/merge';
import { selectTypename } from '@sblive-sports/api-client-js';
import { UPCOMING } from 'constants/GameStatuses';
import { useApplicationContainer } from 'containers/ApplicationContainer';

interface PickEmStorage {
  timestamp: string;
  gameTeamId: string;
}

export const findParticipantByGameTeamId = (contestParticipant: { id: string; participant: { id: string } | {} }, gameTeamId: string) => {
  // required because participant is ContestParticipantUnion
  if ('id' in contestParticipant.participant) {
    return contestParticipant.participant.id === gameTeamId;
  }
};

export interface GameContainerProps {
  query: GamesShowQuery;
  statCategories: Record<string, Record<string, string>>;
}

export const [GameContainer, useGameContainer] = createContainer(({ query: { game: gameProp }, statCategories }: GameContainerProps) => {
  const user = useUser();
  const [, { handleOpenLoginModal }] = useApplicationContainer();
  const [game, setGame] = useState(gameProp);
  const [picks, setPicks] = useState<{ [id: string]: number } | undefined>(
    game.contest?.contestParticipants.reduce((all, team) => {
      return {
        ...all,
        [team.id]: team.votes
      };
    }, {})
  );
  const [pickEmGameTeamId, setPickEmGameTeamId] = useState<string>();
  const [pickEm, setPickEm] = useState<GamePickEmQuery['currentUser']['pickEm'] | null>(null);

  const [showPlayPickEmModal, setShowPlayPickEmModal] = useState<boolean>(false);
  const closePlayPickEmModal = () => setShowPlayPickEmModal(false);
  useEffect(() => {
    if (gameProp.contest?.bracket?.webPath && gameProp.statusId === UPCOMING) {
      setShowPlayPickEmModal(true);
    }
  }, []);

  const PICK_EM_KEY = `cheer-${game.id}`;

  useInterval(
    () => {
      rubyQueries.gamesLiveQuery(30, { id: +game.id }).then(({ game }) => {
        setGame(currentGame => merge(currentGame, game));
      });
    },
    30 * 1000,
    game.statusId === LIVE
  );

  useEffect(() => {
    if (user.isLoggedIn && game.contest?.id) {
      queries.gamePickEmQuery({ contestId: game.contest.id }).then(({ currentUser: { pickEm } }) => {
        if (!pickEm) return;

        const contestParticipant = game.contest?.contestParticipants.find(c => findParticipantByGameTeamId(c, pickEm.team.id));
        if (!contestParticipant) return;

        setPickEmGameTeamId(contestParticipant.id);
        setPickEm(pickEm);
      });
    } else {
      const cheer = localCache.get<PickEmStorage>(PICK_EM_KEY);

      if (cheer?.timestamp && dayjs() > dayjs(cheer.timestamp)) {
        localCache.delete(PICK_EM_KEY);
      } else if (cheer?.gameTeamId) {
        setPickEmGameTeamId(cheer.gameTeamId);
      }
    }
  }, [user.isLoggedIn]);

  function pickTeam(gameTeamId: string | null): void {
    if (gameTeamId === null && pickEm) {
      queries
        .deleteContestPickEmsMutation({ pickEmIds: [pickEm.id] })
        .then(() => {
          setPickEmGameTeamId(undefined);
        })
        .catch(errors => {
          console.error(errors);
        });
      return;
    }

    if (gameTeamId !== null && !game.contest?.id) {
      const contestParticipant = game.contest?.contestParticipants.find(c => c.id === gameTeamId);
      const teamId = contestParticipant?.participant && 'id' in contestParticipant.participant && contestParticipant.participant.id;
      const gameTeamIdFound = game.gameTeams.find(gt => gt.team.id === teamId)?.id;
      if (gameTeamIdFound) oldPickTeam(gameTeamIdFound);
      return;
    }

    if (gameTeamId !== null && game.contest?.id) {
      if (!user.isLoggedIn) {
        handleOpenLoginModal();
      } else {
        queries
          .createContestPickEmMutation({ id: gameTeamId })
          .then(({ createContestPickEm }) => {
            setPicks({
              ...picks,
              ...(pickEmGameTeamId && picks ? { [pickEmGameTeamId]: picks[pickEmGameTeamId] - 1 } : {}),
              [createContestPickEm.id]: createContestPickEm.votes
            });
            setPickEmGameTeamId(gameTeamId);
          })
          .catch(errors => {
            console.error(errors);
          });
      }
    }
  }

  function oldPickTeam(gameTeamId: string) {
    const decrementGameTeamId = pickEmGameTeamId;

    queries
      .cheerGameTeamMutation({ id: gameTeamId, decrement_id: decrementGameTeamId })
      .then(({ cheerGameTeam }) => {
        setPickEmGameTeamId(cheerGameTeam.id);

        // TODO:  update this, if needed, to handle new picks structure
        // TODO:  are we maintaining logged out voting?
        // setPicks(
        //   game.gameTeams.map(gameTeam => {
        //     if (gameTeam.id === cheerGameTeam.id) {
        //       return cheerGameTeam.picks;
        //     } else if (gameTeam.id === decrementGameTeamId) {
        //       return gameTeam.picks - 1;
        //     } else {
        //       return gameTeam.picks;
        //     }
        //   })
        // );

        localCache.set(PICK_EM_KEY, {
          timestamp: dayjs(new Date()).add(1, 'month'),
          gameTeamId: cheerGameTeam.id
        });
      })
      .catch(errors => {
        console.error(errors);
      });
  }

  return [
    {
      game: {
        ...game,
        contest: {
          ...game.contest,
          bracket: game.contest?.bracket,
          bracketRound: game.contest?.bracketRound,
          bracketSeries: game.contest?.bracketSeries,
          expectedStatus: game.contest?.expectedStatus,
          gofanTicketingUrl: game.contest?.gofanTicketingUrl,
          nfhsNetworkUrl: game.contest?.nfhsNetworkUrl,
          contestParticipants: game.contest?.contestParticipants.map(c => ({ ...c, votes: picks?.[c.id] }))
        }
      },
      pickEmGameTeamId,
      statCategories,
      photoGalleries: game.photoGalleries.nodes.filter(selectTypename('ContentItemPhotoGallery')),
      showPlayPickEmModal
    },
    {
      pickTeam,
      closePlayPickEmModal
    }
  ];
});
